// frontend/src/pages/manufacturing/ManualWorkOrderCreation.jsx

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Autocomplete,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Search as SearchIcon,
  Build as BuildIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  ViewList as ViewListIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../services/api';
import debounce from 'lodash/debounce';
import usePageTitle from '../../hooks/usePageTitle';

const ManualWorkOrderCreation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Form state
  const [selectedItem, setSelectedItem] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [dueDate, setDueDate] = useState('');
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  usePageTitle('Create Work Order');

  // Determine active tab based on current path
  const getActiveTab = () => {
    if (location.pathname === '/manufacturing/create-work-order') return 1;
    return 0;
  };

  const handleTabChange = (event, newValue) => {
    if (newValue === 0) {
      navigate('/manufacturing/work-orders');
    } else if (newValue === 1) {
      navigate('/manufacturing/create-work-order');
    }
  };

  // Debounced search function
  const searchItems = useCallback(
    debounce(async (query) => {
      if (!query || query.length < 2) {
        setSearchResults([]);
        return;
      }
      
      setSearching(true);
      try {
        const response = await api.get('/api/items/', {
          params: {
            search: query,
            page_size: 20
          }
        });
        
        const items = response.data.results || response.data;
        setSearchResults(items);
      } catch (err) {
        console.error('Search error:', err);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    searchItems(searchQuery);
  }, [searchQuery, searchItems]);

  const handleSubmit = async () => {
    if (!selectedItem) {
      toast.error('Please select a product');
      return;
    }
    
    const qty = parseInt(quantity);
    if (!qty || qty <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }
    
    setSubmitting(true);
    setError(null);
    setResult(null);
    
    try {
      // Build the customer_order_ref - use order number if provided, otherwise notes or default
      let customerOrderRef = 'Manual Order';
      if (orderNumber.trim()) {
        customerOrderRef = orderNumber.trim();
      } else if (notes.trim()) {
        customerOrderRef = notes.trim();
      }
      
      const response = await api.post('/api/work-orders/create_from_bom_explosion/', {
        item_id: selectedItem.id,
        quantity: qty,
        customer_order_ref: customerOrderRef,
        due_date: dueDate || null
      });
      
      setResult(response.data);
      toast.success(response.data.message || 'Work orders created successfully');
      
      // Clear form after success
      setSelectedItem(null);
      setQuantity('');
      setOrderNumber('');
      setNotes('');
      setDueDate('');
      setSearchQuery('');
      
    } catch (err) {
      console.error('BOM explosion error:', err);
      
      if (err.response?.data?.items_missing_operation) {
        setError({
          type: 'missing_operations',
          message: err.response.data.message,
          items: err.response.data.items_missing_operation
        });
      } else {
        setError({
          type: 'general',
          message: err.response?.data?.error || 'Failed to create work orders'
        });
      }
      
      toast.error('Failed to create work orders');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitSingle = async () => {
    if (!selectedItem) {
      toast.error('Please select a product');
      return;
    }

    const qty = parseInt(quantity);
    if (!qty || qty <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    if (!selectedItem.assembly_operation) {
      toast.error('This item has no assembly operation set. Edit the item first or use BOM Explosion.');
      return;
    }

    setSubmitting(true);
    setError(null);
    setResult(null);

    let customerOrderRef = 'Manual Order';
    if (orderNumber.trim()) {
      customerOrderRef = orderNumber.trim();
    } else if (notes.trim()) {
      customerOrderRef = notes.trim();
    }

    try {
      // Date input gives a YYYY-MM-DD string; backend's DateTimeField needs an
      // aware datetime, otherwise downstream is_overdue comparisons crash.
      const dueDateIso = dueDate ? new Date(`${dueDate}T00:00:00Z`).toISOString() : null;

      const response = await api.post('/api/work-orders/', {
        output_item: selectedItem.id,
        operation: selectedItem.assembly_operation,
        quantity_to_produce: qty,
        priority: 3,
        customer_order_ref: customerOrderRef,
        due_date: dueDateIso,
        notes: notes,
        scheduled_start: new Date().toISOString(),
        scheduled_end: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        consolidate: true,
      });

      // If the backend consolidated into an existing WO, the returned
      // quantity_to_produce will be larger than what we just submitted.
      const totalQty = parseFloat(response.data.quantity_to_produce);
      const wasConsolidated = totalQty > qty;

      setResult({
        message: wasConsolidated
          ? `Existing work order updated: ${response.data.work_order_number} (now ${totalQty} units total)`
          : `Single work order created: ${response.data.work_order_number}`,
        created: wasConsolidated ? 0 : 1,
        consolidated: wasConsolidated ? 1 : 0,
        multi_step: 0,
        work_orders: [{
          work_order_number: response.data.work_order_number,
          action: wasConsolidated ? 'consolidated' : 'created',
          item_code: selectedItem.item_code,
          item_name: selectedItem.name,
          quantity: qty,
          total_quantity: totalQty,
          operation: selectedItem.assembly_operation_display || '',
          step_number: 1,
          total_steps: 1,
        }],
      });
      toast.success(wasConsolidated ? 'Work order updated' : 'Work order created');

      setSelectedItem(null);
      setQuantity('');
      setOrderNumber('');
      setNotes('');
      setDueDate('');
      setSearchQuery('');
    } catch (err) {
      console.error('Single WO error:', err);

      let msg = err.response?.data?.error;
      if (!msg && err.response?.data && typeof err.response.data === 'object') {
        msg = Object.entries(err.response.data)
          .map(([f, m]) => `${f}: ${Array.isArray(m) ? m.join(', ') : m}`)
          .join('\n');
      }
      if (!msg) msg = 'Failed to create work order';

      setError({ type: 'general', message: msg });
      toast.error('Failed to create work order');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setSelectedItem(null);
    setQuantity('');
    setOrderNumber('');
    setNotes('');
    setDueDate('');
    setSearchQuery('');
    setResult(null);
    setError(null);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      {/* Tab Navigation */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={getActiveTab()} 
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab 
            icon={<ViewListIcon />} 
            iconPosition="start" 
            label="Work Orders" 
          />
          <Tab 
            icon={<AddIcon />} 
            iconPosition="start" 
            label="Create Work Order" 
          />
        </Tabs>
      </Paper>
      
      {/* Page Content */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <BuildIcon color="primary" />
          Create Manual Work Order
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Search for a product, enter quantity, then choose:{' '}
          <strong>Create Single Work Order</strong> for one work order on the chosen item only,
          or <strong>Create Work Orders</strong> to also explode the BOM and create child work
          orders for every sub-assembly that lacks stock. Either way, the components in the BOM
          are consumed from stock when the work order is completed on the shop floor.
        </Typography>
        
        <Divider sx={{ mb: 3 }} />
        
        {/* Search & Form */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Item Search */}
          <Autocomplete
            value={selectedItem}
            onChange={(event, newValue) => {
              setSelectedItem(newValue);
              setError(null);
            }}
            inputValue={searchQuery}
            onInputChange={(event, newInputValue) => {
              setSearchQuery(newInputValue);
            }}
            options={searchResults}
            getOptionLabel={(option) => `${option.item_code} - ${option.name}`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={searching}
            filterOptions={(x) => x}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body1" fontWeight="medium">
                    {option.item_code}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {option.name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    <Chip 
                      label={option.item_type?.replace('_', ' ')} 
                      size="small" 
                      variant="outlined"
                    />
                    {option.is_phantom && (
                      <Chip label="Phantom" size="small" color="warning" />
                    )}
                  </Box>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search Product"
                placeholder="Type product code or name..."
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                  endAdornment: (
                    <>
                      {searching ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          
          {/* Selected Item Display */}
          {selectedItem && (
            <Card variant="outlined" sx={{ bgcolor: 'primary.50' }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Selected Product
                </Typography>
                <Typography variant="h6">
                  {selectedItem.item_code} - {selectedItem.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip 
                    label={selectedItem.item_type?.replace('_', ' ')} 
                    size="small"
                  />
                  {selectedItem.assembly_operation_display && (
                    <Chip 
                      label={`Op: ${selectedItem.assembly_operation_display}`} 
                      size="small" 
                      variant="outlined"
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          )}
          
          {/* Quantity & Order Number Row */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Quantity"
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              sx={{ width: 150 }}
              inputProps={{ min: 1 }}
              required
            />
            
            <TextField
              label="Order Number"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              placeholder="e.g., 12345"
              helperText="Displayed on terminals"
              sx={{ width: 200 }}
            />
            
            <TextField
              label="Due Date"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              sx={{ width: 180 }}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          
          {/* Notes Row */}
          <TextField
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g., Stock replenishment, Sample order..."
            fullWidth
          />
          
          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handleReset}
              disabled={submitting}
            >
              Clear
            </Button>
            <Button
              variant="outlined"
              onClick={handleSubmitSingle}
              disabled={!selectedItem || !quantity || submitting}
              size="large"
            >
              Create Single Work Order
            </Button>
            <Button
              variant="contained"
              startIcon={submitting ? <CircularProgress size={20} /> : <AddIcon />}
              onClick={handleSubmit}
              disabled={!selectedItem || !quantity || submitting}
              size="large"
            >
              {submitting ? 'Creating Work Orders...' : 'Create Work Orders'}
            </Button>
          </Box>
        </Box>
      </Paper>
      
      {/* Error Display */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          action={
            error.type === 'missing_operations' && (
              <Button 
                color="inherit" 
                size="small"
                onClick={() => navigate('/manufacturing/items')}
              >
                Go to Items
              </Button>
            )
          }
        >
          <Typography variant="subtitle2">{error.message}</Typography>
          {error.items && (
            <List dense>
              {error.items.map((item, idx) => (
                <ListItem key={idx} sx={{ py: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <WarningIcon color="error" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={`${item.item_code} - ${item.item_name}`}
                    secondary={`Needs ${item.quantity_needed} units`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Alert>
      )}
      
      {/* Success Result */}
      {result && (
        <Paper sx={{ p: 3 }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">{result.message}</Typography>
          </Alert>
          
          <Typography variant="h6" sx={{ mb: 2 }}>
            Work Orders Created/Updated
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Chip 
              label={`${result.created || 0} Created`} 
              color="success" 
              icon={<AddIcon />}
            />
            <Chip 
              label={`${result.consolidated || 0} Consolidated`} 
              color="info"
            />
            {result.multi_step > 0 && (
              <Chip 
                label={`${result.multi_step} Multi-step`} 
                variant="outlined"
              />
            )}
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <List>
            {result.work_orders?.map((wo, idx) => (
              <ListItem 
                key={idx}
                sx={{ 
                  bgcolor: wo.action === 'created' ? 'success.50' : 'info.50',
                  mb: 1,
                  borderRadius: 1
                }}
              >
                <ListItemIcon>
                  <CheckCircleIcon color={wo.action === 'created' ? 'success' : 'info'} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography fontWeight="medium">{wo.work_order_number}</Typography>
                      <Chip 
                        label={wo.action} 
                        size="small" 
                        color={wo.action === 'created' ? 'success' : 'info'}
                      />
                      {wo.total_steps > 1 && (
                        <Chip 
                          label={`Step ${wo.step_number}/${wo.total_steps}`} 
                          size="small" 
                          variant="outlined"
                        />
                      )}
                    </Box>
                  }
                  secondary={`${wo.item_code} - ${wo.item_name} | Qty: ${wo.quantity} | Op: ${wo.operation}`}
                />
              </ListItem>
            ))}
          </List>
          
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              onClick={() => navigate('/manufacturing/work-orders')}
            >
              View Work Orders
            </Button>
            <Button
              variant="outlined"
              onClick={handleReset}
            >
              Create Another
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default ManualWorkOrderCreation;